<script setup lang="ts">
import {
  createDefaultColorOptions,
  createMarkupEditorToolbar,
  createMarkupEditorToolStyles,
  getEditorDefaults
} from '@pqina/pintura'
import localeDeDE, {
  MarkupEditor as localeMarkupEditorDeDE
} from '@pqina/pintura/locale/de_DE/core/de_DE.js'
import localeAnnotateDeDE from '@pqina/pintura/locale/de_DE/annotate/de_DE.js'
import localeCropDeDE from '@pqina/pintura/locale/de_DE/crop/de_DE.js'
import localeFilterDeDE from '@pqina/pintura/locale/de_DE/filter/de_DE.js'
import localeFinetuneDeDE from '@pqina/pintura/locale/de_DE/finetune/de_DE.js'
import localeResizeDeDE from '@pqina/pintura/locale/de_DE/resize/de_DE.js'
import moment from 'moment'
import { enabled } from 'virtual:nuxt-pwa-configuration'
import { globalToast } from '~/utils/toast.utils'
import 'moment/dist/locale/de'

const { siteName, appVersion } = useAppConfig()
useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk} - ${siteName}` : siteName
  },
  meta: [
    { name: 'version', content: `${appVersion}` },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    }
  ]
})

const { $pwa } = useNuxtApp()
const { t } = useL10n()
const toast = useToast()
// useApi() runs outside a component, where useToast() is not available, so it
// writes its error messages through this ref instead. Nothing ever assigned
// it, so every API error the portal reported was dropped. Assigned here in
// setup rather than onMounted: child pages mount first and already call the
// API from their own setup.
globalToast.value = toast
const user = useUserStore()
const languageStore = useLanguageStore()
const customFieldStore = useCustomFieldStore()
const notifications = useNotifications()

const applyDefaultColor = () => {
  useCssVar('--primary-color-50').value = '#d5e9eb'
  useCssVar('--primary-color-100').value = '#abd3d8'
  useCssVar('--primary-color-200').value = '#abd3d8'
  useCssVar('--primary-color-300').value = '#90c5cb'
  useCssVar('--primary-color-400').value = '#74b6be'
  useCssVar('--primary-color-500').value = '#58a8b1'
  useCssVar('--primary-color-600').value = '#43878e'
  useCssVar('--primary-color-700').value = '#316368'
  useCssVar('--primary-color-800').value = '#1f3e42'
  useCssVar('--primary-color-900').value = '#0d1a1c'
  useCssVar('--primary-color-950').value = '#040809'
  useCssVar('--p-primary-50').value = '#d5e9eb'
  useCssVar('--p-primary-100').value = '#abd3d8'
  useCssVar('--p-primary-200').value = '#abd3d8'
  useCssVar('--p-primary-300').value = '#90c5cb'
  useCssVar('--p-primary-400').value = '#74b6be'
  useCssVar('--p-primary-500').value = '#58a8b1'
  useCssVar('--p-primary-600').value = '#43878e'
  useCssVar('--p-primary-700').value = '#316368'
  useCssVar('--p-primary-800').value = '#1f3e42'
  useCssVar('--p-primary-900').value = '#0d1a1c'
  useCssVar('--p-primary-950').value = '#040809'
  useCssVar('--p-primary-color').value = '#58a8b1'
  useCssVar('--primary-color').value = '#58a8b1'
  useCssVar('--surface-500').value = '#f5f5f5'
}

onMounted(async () => {
  applyDefaultColor()
  provideGlobalState()

  // Force service worker registration and wait for it to be ready
  if (enabled && 'serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.ready
    } catch (error) {
      toast.add({ severity: 'warn', detail: 'Offline features may not work' })
    }
  }

  if (!user.isLoggedIn && !user.isLoggedInAsContact) {
    navigateTo('/login')
    toast.add({
      severity: 'warn',
      summary: t('pages.login.notSignedIn'),
      detail: t('pages.login.notSignedInDetail'),
      life: 5000
    })
  }

  if (user.isLoggedIn) {
    const mandantSettings = await useApi('tenant/settings', 'GET')
    customFieldStore.setData(mandantSettings?.data ?? {})

    user.user.mid = (await useApi('tenant', 'GET'))?.data?.mid

    const hasSuperVisor = await useApi(`users/${user.user.id}`, 'GET')?.then(
      (res) => !!res?.data?.supervisor
    )
    user.setHasSupervisor(hasSuperVisor)
  }

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  menuItems.value = [
    {
      label: t('global.userMenu.reload'),
      icon: 'pi pi-refresh',
      command: () => {
        $pwa?.updateServiceWorker(true)
      }
    },
    {
      label: t('global.userMenu.logout'),
      icon: 'pi pi-sign-out',
      command: async () => {
        // Unsubscribe from notifications before logout
        if (notifications.isSubscribed.value) {
          try {
            await notifications.unsubscribe()
          } catch {}
        }

        user.calendarCategories = null
        user.setHasSupervisor(null)

        user.logout()
        navigateTo('/login')
      }
    }
  ]

  imageEditorProps.value = getEditorDefaults()

  if (languageStore.language === 'german') {
    moment.locale('de')
    imageEditorProps.value.locale = {
      ...localeDeDE,
      ...localeMarkupEditorDeDE,
      ...localeAnnotateDeDE,
      ...localeCropDeDE,
      ...localeFilterDeDE,
      ...localeFinetuneDeDE,
      ...localeResizeDeDE
    }
  }

  imageEditorProps.value.enableZoomControls = false

  imageEditorProps.value.utils = ['annotate', 'crop', 'filter', 'finetune', 'resize']

  imageEditorProps.value.markupEditorToolbar = createMarkupEditorToolbar([
    'ellipse',
    'arrow',
    'rectangle',
    'line',
    'text',
    'preset'
  ])

  imageEditorProps.value.markupEditorToolStyles = createMarkupEditorToolStyles({
    marker: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    ellipse: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    rectangle: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    arrow: {
      strokeWidth: 12
    },
    text: {
      fontSize: 48
    }
  })
})
</script>
<template>
  <div>
    <NuxtLayout :key="languageStore.language">
      <VitePwaManifest />
      <NuxtPage />
    </NuxtLayout>

    <!-- Without this nothing renders what useToast() queues, so every message
         the portal writes — an upload confirmed, an appointment saved, a share
         that fell back to a download — was queued and never seen. Left at the
         default position: the .p-toast rule further down already places it
         under the app shell header. -->
    <Toast />
  </div>
</template>
<style lang="scss">
body {
  background-color: var(--portal-bg);
}
div {
  line-height: normal;
}
.p-card {
  background-color: white !important;
  border-radius: 0.25rem !important;
  padding: 0.75rem !important;
}
.p-card-body {
  padding: 4px !important;
}
.p-card-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 0;
  padding-bottom: 0;
}
.p-component {
  font-size: 0.875rem;
}
.p-inputtext::placeholder,
.p-textarea::placeholder,
.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.p-multiselect .p-multiselect-label.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.error {
  color: crimson;
  font-weight: 500;
}
.p-select {
  z-index: 1 !important;
}
.p-select-option-label {
  white-space: pre-line;
}
.p-toast {
  position: fixed;
  // Clears the app shell header, which is sticky at the top of the viewport.
  top: calc(var(--portal-header-height) + 0.5rem);
  right: 0;
  left: 20px;
  z-index: 1000;
  width: 80% !important;
}
.image-editor {
  height: 24rem;
}
.fieldTitle {
  color: var(--primary-color);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.buttonHeader {
  background-color: var(--primary-500) !important;
  border: 0 !important;
  padding: 0.5rem 1.8rem !important;
}
.p-button:not(.p-button-outlined):not(.p-button-text):not(.p-button-link) .p-button-label {
  color: white !important;
}
.p-menu-item-label,
.p-menu-item-icon {
  color: var(--text-color) !important;
}
.p-iftalabel {
  input {
    border-radius: 14px;
    padding: 1rem;
  }
  label {
    margin-top: 0.3rem;
    margin-left: 0.4rem;
  }
}
.groupTitle {
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: black;
}
.selectInput {
  width: 100%;
  height: 3.5rem;
  align-items: center;

  .p-datepicker-input,
  .p-inputnumber-input,
  button {
    height: 100%;
  }
}
.checkboxGroup {
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.PinturaRoot .PinturaButtonExport {
  background-color: var(--primary-500) !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}

.PinturaButtonIconOnly {
  background-color: var(--primary-500) !important;
  border-color: white !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}
.PinturaButton[disabled] {
  background-color: #a8cfd5 !important;
  filter: none !important;

  .PinturaButtonInner {
    color: white !important;
  }

  svg {
    opacity: 0.75 !important;
  }
}
.submitButton {
  width: 100% !important;
  padding: 1.25rem !important;
  border-radius: 30px !important;
  margin-top: 1.25rem !important;
}
.signaturePad,
canvas {
  border-style: solid;
  border-width: 1px;
  border-color: #d1d5db;
  background-color: white;
  border-radius: 14px;
  width: 100% !important;
}
.p-multiselect {
  background: #ffffff;
  height: min-content;

  .p-multiselect-label {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    color: #495057 !important;
  }
}

.p-multiselect-filter {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.875rem;
}

.p-multiselect-dropdown,
.p-select-dropdown {
  width: unset;
  min-width: 3rem;
  padding-right: 0.5rem;
  pointer-events: auto;
}
.changeInputTypeButton {
  border: none !important;
  background-color: transparent !important;
  padding: 0 !important;

  .p-button-label {
    color: var(--primary-color) !important;
  }
  &:hover {
    background-color: transparent !important;
    border: none !important;
  }
}
.tabs-container {
  width: 100%;
  overflow: hidden;
}
.tabs-container .p-tab {
  width: 50%;
  height: 68px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-width: 3px 0 0 0;

  span {
    color: #64748b;
    font-weight: 600;
  }

  i {
    font-size: 1.5rem;
  }
}
.p-tab-active span {
  color: var(--primary-color) !important;
}
.p-select {
  outline: none;
}
.p-select:focus {
  outline: none;
}
.p-multiselect {
  outline: none;
}
.p-multiselect:focus {
  outline: none;
}
.p-textarea,
.p-inputtext {
  font-size: 14px !important;
}
</style>
